import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { assetUrl } from "fusion-core";
import { Helmet } from "fusion-plugin-react-helmet-async";
import BuyStep from "../enums/BuyStep";
import { useNavigate } from "fusion-plugin-react-router";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import QuoteStepHeading from "../../../shared/components/QuoteStepHeading";
import QuoteStepLoader from "../../../shared/components/QuoteStepLoader";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import useBuyStore from "../store/BuyStore";
import { useForm } from "react-hook-form";
import { fetchHpp } from "../../quote/services/quoteService";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import useAppStateStore from "../../../shared/store/AppStateStore";
import { scrollToTop } from "../../../shared/utils/scrollUtils";
import { handlePaymentSuccess } from "../services/buyPaymentFlowService";
import ModalWrapper from "../../../shared/components/ModalWrapper";
import GlobalPaymentsIframe from "../components/GlobalPaymentsIframe";
import { getDeclineMessage } from "../utils/resultCodes";

const TbGlobalPaymentsPage = () => {
  const navigate = useNavigate();
  let STATE = useAppStateStore();
  const [hppUrl, setHppUrl] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isErrorModal, setIsErrorModal] = useState(false);
  const [paymentHandled, setPaymentHandled] = useState(false);

  const buyStore = useBuyStore.getState();
  const selectedPaymentOption = buyStore.getObject("selectedPaymentOption");
  const paymentJson = buyStore.getObject("jsonPaymentData");

  const { watch, setValue } = useForm();

  const paymentsCss = assetUrl("../styles/payments.css");

  const handleSuccess = (response) => {
    if (paymentHandled) return;
    setPaymentHandled(true);
    setErrorMessage("");
    handlePaymentSuccess(
      response,
      navigate,
      STATE.setPaymentProcessLoading,
      setValue,
      watch
    );
  };

  const handleFailure = (response) => {
    if (paymentHandled) return;
    setPaymentHandled(true);
    setErrorMessage(getDeclineMessage(response.RESULT));
    setIsErrorModal(true);
  };

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TBGLOBALPAYMENTSPAGE_ENTERED);
  }, []);

  useEffect(() => {
    scrollToTop();
    let didTimeout = false;
    const timeoutId = setTimeout(() => {
      didTimeout = true;
      Sentry.captureMessage("GlobalPayments HPP URL fetch timeout");
      navigate("/blocked-action?type=payment-too-long");
    }, 20000);

    (async () => {
      try {
        const { hppUrl } = await fetchHpp();
        if (!didTimeout) {
          clearTimeout(timeoutId);
          setHppUrl(hppUrl);
        }
      } catch (err) {
        if (!didTimeout) {
          clearTimeout(timeoutId);
          console.error("Failed to fetch HPP URL", err);
          Sentry.captureException(
            new Error(
              `GlobalPayments: ${err && err.message ? err.message : err}`
            )
          );
          navigate("/blocked-action?type=payment-no-load");
        }
      }
    })();

    return () => clearTimeout(timeoutId);
  }, []);

  const handleRetry = () => {
    setPaymentHandled(false);
    STATE.setCurrentStep(BuyStep.TB_E_PAYMENT);
    setIsErrorModal(false);
  };

  const getPreviousStep = () => {
    const paymentType = selectedPaymentOption?.totalAmountToPayByCustomer
      ? "annual"
      : "monthly";
    return paymentType === "annual"
      ? BuyStep.TB_E_PAYMENT
      : BuyStep.TB_DIRECT_DEBIT_DETAILS;
  };

  if (
    !errorMessage &&
    (STATE.paymentProcessLoading || !paymentJson || !hppUrl)
  ) {
    return (
      <QuoteStepLoader
        title="Payment Details"
        description="Enter your payment details below."
        message={
          STATE.paymentProcessLoading
            ? "Processing your payment..."
            : "Loading payment details..."
        }
      />
    );
  }

  return (
    <>
      {isErrorModal && (
        <ModalWrapper
          heading="Payment Failed"
          buttonText="Try again"
          onButtonClick={handleRetry}
          showClose={false}
          simpleText={`Reason: ${errorMessage}`}
          onClose={() => setIsErrorModal(false)}
        />
      )}

      <form>
        <Helmet>
          <meta name="description" content="Redirecting to payment page" />
          <link rel="stylesheet" href={paymentsCss} />
        </Helmet>

        <QuoteFormWrapper>
          <QuoteStepHeading
            title="Payment Details"
            description="Enter your payment details below."
          />

          <GlobalPaymentsIframe
            hppUrl={hppUrl}
            paymentJson={paymentJson}
            onSuccess={handleSuccess}
            onFailure={handleFailure}
          />
        </QuoteFormWrapper>

        <FormNavigationButtons
          prevStep={getPreviousStep()}
          nextStep={false}
          isValid={true}
        />
      </form>
    </>
  );
};

export default TbGlobalPaymentsPage;
